import React, { useState, useEffect } from 'react';
import axios from 'axios';

const UserPermissionsModal = ({ isOpen, onClose, user, onUpdated }) => {
  const [allPermissions, setAllPermissions] = useState([]);
  const [userPermNames, setUserPermNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isOpen && user) {
      fetchPermissions();
      fetchUserPermissions();
    }
  }, [isOpen, user]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/permissions', {
        headers: { 'X-Session-Token': localStorage.getItem('session_token') || '' }
      });
      const perms = res.data && (res.data.permissions || res.data) ? res.data.permissions || res.data : {};
      // flatten permissions object into array
      const flat = [];
      if (Array.isArray(perms)) {
        flat.push(...perms);
      } else if (typeof perms === 'object') {
        Object.keys(perms).forEach(cat => {
          const arr = perms[cat] || [];
          arr.forEach(p => flat.push({ ...p, category: cat }));
        });
      }
      // Ensure ticket.scanner exists so admins can grant scanner from this modal
      const hasScanner = flat.some(p => (p.permission_name || p.name || p) === 'ticket.scanner');
      if (!hasScanner) {
        flat.unshift({ permission_name: 'ticket.scanner', permission_label: 'Ticket Scanner', description: 'Allow access to ticket scanner', category: 'Other' });
      }
      setAllPermissions(flat);
    } catch (err) {
      console.error('Error fetching permissions', err);
      setMessage('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPermissions = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`/api/users/${user.id}/permissions`, {
        headers: { 'X-Session-Token': localStorage.getItem('session_token') || '' }
      });
      let perms = [];
      if (res.data) {
        if (Array.isArray(res.data.permissions)) perms = res.data.permissions;
        else if (Array.isArray(res.data)) perms = res.data;
        else if (res.data.userPermissions && Array.isArray(res.data.userPermissions)) perms = res.data.userPermissions;
      }
      // normalize to names
      const names = perms.map(p => p.permission_name || p.name || p.permissionName).filter(Boolean);
      setUserPermNames(names);
    } catch (err) {
      // fallback: try single-permission check endpoint for scanner exists elsewhere
      console.warn('Could not fetch user permissions, user-specific endpoint may not exist', err);
      setUserPermNames([]);
    }
  };

  const handleToggle = async (permName, checked) => {
    if (!user) return;
    setMessage('');
    try {
      if (checked) {
        try {
          await axios.post(`/api/users/${user.id}/permissions/grant`, { permission_name: permName }, {
            headers: { 'X-Session-Token': localStorage.getItem('session_token') || '' }
          });
        } catch (err) {
          // If permission does not exist in DB, create it then retry
          if (err.response && err.response.status === 404) {
            try {
              await axios.post('/api/permissions', {
                permission_name: permName,
                permission_label: permName.split('.').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' '),
                description: 'Auto-created permission',
                category: 'Other'
              }, { headers: { 'X-Session-Token': localStorage.getItem('session_token') || '' } });
              // retry grant
              await axios.post(`/api/users/${user.id}/permissions/grant`, { permission_name: permName }, {
                headers: { 'X-Session-Token': localStorage.getItem('session_token') || '' }
              });
            } catch (err2) {
              console.error('Error creating then granting permission', err2);
              setMessage('Failed to create and grant permission');
              throw err2;
            }
          } else {
            throw err;
          }
        }
        setUserPermNames(prev => Array.from(new Set([...prev, permName])));
        setMessage('Permission granted');
      } else {
        await axios.post(`/api/users/${user.id}/permissions/revoke`, { permission_name: permName }, {
          headers: { 'X-Session-Token': localStorage.getItem('session_token') || '' }
        });
        setUserPermNames(prev => prev.filter(n => n !== permName));
        setMessage('Permission revoked');
      }
      if (typeof onUpdated === 'function') onUpdated();
    } catch (err) {
      console.error('Error updating permission', err);
      setMessage('Failed to update permission');
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Permissions for {user.name || user.email}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {message && <div className="alert alert-info">{message}</div>}
            {loading ? (
              <div className="text-center py-4">Loading permissions...</div>
            ) : (
              <div>
                {allPermissions.length === 0 ? (
                  <p className="text-muted">No permissions available</p>
                ) : (
                  <div className="list-group">
                    {allPermissions.map((p) => (
                      <label key={p.permission_name || p.id || p.permissionLabel} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{p.permission_label || p.permissionLabel || p.permission_name}</strong>
                          <div className="text-muted small">{p.permission_name || p.permissionName}</div>
                        </div>
                        <input
                          type="checkbox"
                          checked={userPermNames.includes(p.permission_name || p.permissionName || p.name)}
                          onChange={(e) => handleToggle(p.permission_name || p.permissionName || p.name, e.target.checked)}
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserPermissionsModal;
